// Annotation system type definitions

export interface Point {
  x: number
  y: number
}

export type AnnotationTool = 
  | 'pen' 
  | 'arrow' 
  | 'line' 
  | 'rectangle' 
  | 'circle' 
  | 'text'
  | 'select'

export interface Annotation {
  id: string
  type: Exclude<AnnotationTool, 'select'>
  points: Point[]
  color: string
  size: number
  text?: string // For text annotations
  createdAt: string
  updatedAt?: string
}

export interface AnnotationState {
  annotations: Annotation[]
  currentTool: AnnotationTool
  selectedAnnotationId: string | null
  toolColor: string
  toolSize: number
  history: Annotation[][]
  historyIndex: number
}

export interface AnnotationActions {
  addAnnotation: (annotation: Annotation) => void
  updateAnnotation: (id: string, updates: Partial<Annotation>) => void
  deleteAnnotation: (id: string) => void
  selectAnnotation: (id: string | null) => void
  setTool: (tool: AnnotationTool) => void
  setToolColor: (color: string) => void
  setToolSize: (size: number) => void
  undo: () => void
  redo: () => void
  clear: () => void
  exportAnnotations: () => Annotation[]
  importAnnotations: (annotations: Annotation[]) => void
}

// Screenshot-related types
export interface Screenshot {
  id: string
  dataUrl: string
  annotations: Annotation[]
  createdAt: string
  sessionId: string
  createdBy: string
}

// Predefined color palette
export const ANNOTATION_COLORS = [
  '#ef4444', // Red
  '#f97316', // Orange  
  '#eab308', // Yellow
  '#22c55e', // Green
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#000000', // Black
  '#ffffff', // White
  '#6b7280'  // Gray
]

// Predefined sizes
export const ANNOTATION_SIZES = [
  { value: 2, label: 'Thin' },
  { value: 4, label: 'Normal' },
  { value: 6, label: 'Thick' },
  { value: 8, label: 'Very Thick' }
]

// Tool configuration
export const TOOL_CONFIG = {
  pen: {
    name: 'Pen',
    icon: '✏️',
    description: 'Free-form drawing',
    cursor: 'crosshair'
  },
  arrow: {
    name: 'Arrow',
    icon: '➡️',
    description: 'Draw arrows to point at things',
    cursor: 'crosshair'
  },
  line: {
    name: 'Line',
    icon: '📏',
    description: 'Draw straight lines',
    cursor: 'crosshair'
  },
  rectangle: {
    name: 'Rectangle',
    icon: '⬛',
    description: 'Draw rectangles',
    cursor: 'crosshair'
  },
  circle: {
    name: 'Circle',
    icon: '⭕',
    description: 'Draw circles',
    cursor: 'crosshair'
  },
  text: {
    name: 'Text',
    icon: 'T',
    description: 'Add text annotations',
    cursor: 'text'
  },
  select: {
    name: 'Select',
    icon: '👆',
    description: 'Select and move annotations',
    cursor: 'pointer'
  }
} as const